import { Link, createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import {
  Badge,
  buttonVariants,
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@bookaburra/ui';
import { Alert, AlertDescription, AlertTitle } from '@bookaburra/ui';
import { AlertTriangle, ArrowDown, ArrowRight, CalendarDays, MessageCircle, Phone } from 'lucide-react';
import { fetchBookingTenant, fetchServices } from '../booking-flow/api';
import { LocaleSwitcher } from '../components/LocaleSwitcher';
import { BusinessAddressMap } from '../components/BusinessAddressMap';
import { atOurPlaceLabel } from '../lib/address';

interface BookingSearch {
  locale?: string;
}

export const Route = createFileRoute('/book/$slug/')({
  validateSearch: (search: Record<string, unknown>): BookingSearch => ({
    locale: typeof search.locale === 'string' ? search.locale : undefined,
  }),
  component: ServiceList,
});

function ServiceList() {
  const { slug } = Route.useParams();
  const { locale } = Route.useSearch();
  const { data, isLoading, error } = useQuery({
    queryKey: ['services', slug, locale ?? 'default'],
    queryFn: () => fetchServices(slug, locale),
  });
  const tenantQ = useQuery({
    queryKey: ['booking-tenant', slug],
    queryFn: () => fetchBookingTenant(slug),
    staleTime: 60_000,
  });
  const tenantAddress = tenantQ.data?.tenant.address ?? null;
  const hasVoice = tenantQ.data?.tenant.hasVoiceAgent ?? false;
  const activeLocale = data?.locale ?? locale ?? 'en-AU';

  const tenantName = tenantQ.data?.tenant.name ?? null;
  // Tenants without published business hours can't accept bookings —
  // the server refuses POST /book, the chat + voice APIs refuse the
  // handoff, and the per-service calendar would have no information
  // to base the closed-day check on. Hide the booking surfaces and
  // explain why. `tenantQ.isLoading` guard prevents the banner
  // flickering during the initial fetch.
  const hoursConfigured = !tenantQ.isLoading && Boolean(tenantQ.data?.tenant.businessHours);
  const hoursPending = tenantQ.isLoading;

  return (
    <main className="mx-auto max-w-5xl px-6 py-12 sm:px-12">
      {/*
        Page IA: the heading is the booking action itself (not "Choose
        a service"). Below it we surface three equally-weighted ways
        to book — Call, Chat, and Online (which anchor-scrolls to the
        services list). The services grid then sits under its own h2,
        framing it as the third option's destination rather than the
        whole purpose of the page.
      */}
      <header className="mb-10 flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-primary">Book online</p>
          <h1 className="mt-2 text-3xl font-bold tracking-tight sm:text-5xl">
            {tenantName ? `Book with ${tenantName}` : 'Book your appointment'}
          </h1>
          {hoursConfigured ? (
            <>
              <p className="mt-3 max-w-xl text-base text-muted-foreground">
                Three ways to book — pick whatever suits you.
              </p>
              <div className="mt-6 grid gap-3 sm:grid-cols-3">
                {hasVoice ? (
                  <BookingMethodCard
                    to="/voice/$slug"
                    slug={slug}
                    icon={Phone}
                    title="Call"
                    description="Talk to our assistant"
                  />
                ) : null}
                <BookingMethodCard
                  to="/chat/$slug"
                  slug={slug}
                  icon={MessageCircle}
                  title="Chat"
                  description="Quick guided picks"
                />
                <BookingMethodCard
                  anchor="#services"
                  icon={CalendarDays}
                  title="Online"
                  description="Browse services"
                />
              </div>
            </>
          ) : null}
          {tenantAddress ? (
            // Address as a tap-target pill. Click → fetch lng/lat once
            // (Mapbox Geocoding), then render a Static Images URL.
            // Both calls are deferred until the caller actually wants
            // to see the map, so the booking page itself never pays
            // Mapbox costs.
            <BusinessAddressMap address={tenantAddress} />
          ) : null}
        </div>
        <LocaleSwitcher activeLocale={activeLocale} />
      </header>

      {!hoursPending && !hoursConfigured ? (
        <Alert className="mb-10 border-amber-500/40 bg-amber-50 text-amber-900">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          <AlertTitle>Bookings paused</AlertTitle>
          <AlertDescription>
            {tenantName ? `${tenantName} is` : 'This business is'} still
            being set up — their opening hours haven&rsquo;t been published
            yet, so we&rsquo;re not accepting bookings on their behalf.
            Try again later, or contact them directly.
          </AlertDescription>
        </Alert>
      ) : null}

      {hoursConfigured ? (
      <section id="services" className="scroll-mt-6">
        <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
          <div>
            <h2 className="text-2xl font-bold tracking-tight">
              <ArrowDown className="mr-1 inline h-5 w-5 text-muted-foreground" aria-hidden />
              Services
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Pick a service to see times. Each option only shows when the
              right staff member and room are available.
            </p>
          </div>
        </div>

      {isLoading ? <SkeletonGrid /> : null}
      {error ? <p className="text-sm text-destructive">Couldn&rsquo;t load services. Try again.</p> : null}

      {data ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {data.services.map((s) => (
            <Link
              key={s.id}
              to="/book/$slug/$serviceId"
              params={{ slug, serviceId: s.id }}
              search={locale ? { locale } : undefined}
              className="group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-lg"
            >
              <Card className="h-full transition-colors group-hover:border-primary/60">
                <CardHeader>
                  <CardTitle className="flex items-start justify-between gap-2">
                    <span>{s.name}</span>
                    <LocationBadge type={s.locationType} tenantAddress={tenantAddress} />
                  </CardTitle>
                  <CardDescription>
                    {s.durationMinutes} min
                    {s.clientEligibility === 'new_only' ? (
                      <span className="ml-2 inline-block rounded-full bg-brand-sage/10 px-2 py-0.5 text-xs font-medium text-brand-moss">
                        New clients only
                      </span>
                    ) : null}
                    {s.clientEligibility === 'returning_only' ? (
                      <span className="ml-2 inline-block rounded-full bg-brand-sage/10 px-2 py-0.5 text-xs font-medium text-brand-moss">
                        Existing clients only
                      </span>
                    ) : null}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {s.staffSlots.length > 0 ? (
                    <p className="text-xs text-muted-foreground">
                      Needs:{' '}
                      {s.staffSlots
                        .map((slot) => {
                          // Prefer human skill names; fall back to IDs
                          // if a deployed Worker hasn't rolled out the
                          // skillNames field yet.
                          const labels =
                            (slot.skillNames?.length ? slot.skillNames : slot.skills);
                          const skills = labels.join(' + ') || 'any staff';
                          return slot.count > 1 ? `${skills} ×${slot.count}` : skills;
                        })
                        .join(', ')}
                    </p>
                  ) : null}
                </CardContent>
                <CardFooter>
                  <span className="inline-flex items-center gap-1 text-sm font-medium text-primary">
                    Choose times <ArrowRight className="h-4 w-4" />
                  </span>
                </CardFooter>
              </Card>
            </Link>
          ))}
        </div>
      ) : null}
      </section>
      ) : null}
    </main>
  );
}

interface BookingMethodCardProps {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  description: string;
  to?: '/chat/$slug' | '/voice/$slug';
  slug?: string;
  anchor?: string;
}

/**
 * Equal-weight CTA card for one of the three booking methods. Styled
 * as a primary-tinted panel so all three read as siblings, not the
 * usual heavy primary button (which would visually dominate the
 * services grid below).
 *
 * The third method ("Online") is anchor-only: clicking it scrolls
 * smoothly to the services grid further down the page. The first two
 * route to the chat / voice routes.
 */
function BookingMethodCard({
  icon: Icon,
  title,
  description,
  to,
  slug,
  anchor,
}: BookingMethodCardProps) {
  const className =
    'group flex h-full flex-col rounded-2xl border border-brand-sage/15 bg-card p-5 shadow-brand transition-colors hover:border-primary/50 hover:bg-primary/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2';
  const inner = (
    <>
      <Icon className="h-6 w-6 text-primary" />
      <p className="mt-3 text-base font-semibold text-foreground">{title}</p>
      <p className="mt-1 text-sm text-muted-foreground">{description}</p>
    </>
  );
  if (to && slug) {
    return (
      <Link to={to} params={{ slug }} className={className}>
        {inner}
      </Link>
    );
  }
  if (anchor) {
    return (
      <a href={anchor} className={className}>
        {inner}
      </a>
    );
  }
  return null;
}

function LocationBadge({
  type,
  tenantAddress,
}: {
  type: 'on_premises' | 'on_site' | 'virtual' | 'phone';
  tenantAddress: string | null;
}) {
  if (type === 'virtual') return <Badge variant="secondary">Virtual</Badge>;
  if (type === 'phone') return <Badge variant="secondary">Phone</Badge>;
  if (type === 'on_site') return <Badge variant="warning">At your place</Badge>;
  // On-premises: suburb only as the visible label, full address on
  // hover via the native `title` tooltip. Mobile callers who want the
  // full street form can also tap the address pill above the service
  // grid (BusinessAddressMap) — keeping the badge non-interactive
  // here avoids nesting a button inside the card-wide <Link>.
  const suburb = atOurPlaceLabel(tenantAddress);
  return (
    <Badge
      variant="outline"
      title={tenantAddress ?? 'At our place'}
      aria-label={tenantAddress ? `At ${tenantAddress}` : 'At our place'}
    >
      {suburb}
    </Badge>
  );
}

function SkeletonGrid() {
  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="h-48 animate-pulse rounded-lg border border-border bg-secondary" />
      ))}
    </div>
  );
}
